import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, Play, RefreshCw } from "lucide-react";
import { NotificationTemplatesList } from "./NotificationTemplatesList";
import { NotificationTemplateForm } from "./NotificationTemplateForm";
import { NotificationActivityLog } from "./NotificationActivityLog";
import SeriesInterestSettingsCard from "./SeriesInterestSettingsCard";
import { useNotificationActivityLog } from "@/hooks/useNotificationActivityLog";
import { NotificationTemplate } from "@/hooks/useNotificationTemplates";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function NotificationsTab() {
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);
  const [showTriggerConfirm, setShowTriggerConfirm] = useState(false);
  const { triggerProcessing, isTriggering, refetch } = useNotificationActivityLog();

  const handleEdit = (template: NotificationTemplate) => {
    setEditingTemplate(template);
    setShowForm(true);
  };

  const handleDuplicate = (template: NotificationTemplate) => {
    // Create a copy without the id, and rename it. Copies are always plain
    // ticket reminders — standalone status belongs to the function-owned original.
    const duplicated = {
      ...template,
      id: undefined,
      name: `${template.name} (Copy)`,
      template_type: "ticket_reminder",
    } as NotificationTemplate;
    setEditingTemplate(duplicated);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingTemplate(null);
  };

  const handleTriggerConfirm = () => {
    triggerProcessing();
    setShowTriggerConfirm(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="h-6 w-6 text-primary" />
              <div>
                <CardTitle>Notifications</CardTitle>
                <CardDescription>
                  Configure automated notifications sent before events
                </CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowTriggerConfirm(true)}
                disabled={isTriggering}
                className="gap-2"
              >
                <Play className="h-4 w-4" />
                {isTriggering ? "Processing..." : "Run Now"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {showForm ? (
            <NotificationTemplateForm
              template={editingTemplate}
              onClose={handleFormClose}
            />
          ) : (
            <>
              <NotificationTemplatesList
                onEdit={handleEdit}
                onCreateNew={() => setShowForm(true)}
                onDuplicate={handleDuplicate}
              />
              <SeriesInterestSettingsCard />
              <NotificationActivityLog />
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={showTriggerConfirm} onOpenChange={setShowTriggerConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Run Notification Processing</AlertDialogTitle>
            <AlertDialogDescription>
              This will immediately check for any notifications that need to be sent based on 
              upcoming events and active templates. Are you sure you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleTriggerConfirm}>
              Run Now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
